import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle,
  Lightning,
  ArrowLeft,
} from "phosphor-react";
import { loginWithGoogle } from "../auth";

export default function Pricing() {
  const handleGetStarted = async () => {
    await loginWithGoogle('/dashboard');
  };

  const features = {
    free: [
      "10 jobs per month",
      "5 photos per job",
      "Unlimited customers",
      "Basic calendar",
      "Estimates & Invoices",
      "Technician management",
      "Support tickets",
    ],
    addons: [
      { name: "Appointment Reminders", desc: "Email & SMS reminders", price: "$9/mo" },
      { name: "Spanish Translation", desc: "AI-powered translations", price: "$9/mo" },
      { name: "Email Marketing", desc: "Campaigns & analytics", price: "$19/mo" },
      { name: "Review Collection", desc: "Automated review requests", price: "$9/mo" },
      { name: "Payment Processing", desc: "Accept card payments", price: "$19/mo" },
      { name: "Online Booking", desc: "Customer booking widget", price: "$9/mo" },
      { name: "Unlimited Usage", desc: "Remove all limits", price: "$29/mo" },
    ],
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white">
      <div className="fixed inset-0 grain-texture pointer-events-none" />

      {/* Header with warm gradient background for logo visibility */}
      <header className="relative z-10 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
              alt="Free Service Pro"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-sm text-zinc-700 hover:text-[#FF6600] transition-colors font-medium">
              <ArrowLeft size={18} className="inline mr-2" />
              Back
            </Link>
            <Link to="/login" className="text-sm text-zinc-700 hover:text-[#FF6600] transition-colors font-medium">
              Login
            </Link>
          </nav>
        </div>
      </header>
      
      {/* Gradient transition from header */}
      <div className="relative z-10 h-24 bg-gradient-to-b from-amber-50 via-amber-50/30 to-transparent -mb-24" />

      {/* Main Content */}
      <main className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6600]/10 border-2 border-[#FF6600]/30 rounded-sm mb-6">
              <Lightning size={20} weight="fill" className="text-[#FF6600]" />
              <span className="text-sm font-medium text-[#FF6600]">
                FREE FOREVER PLAN
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
              Simple, <span className="text-[#FF6600]">Transparent</span> Pricing
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Start free, add what you need. No surprise charges or hidden fees.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <Card className="bg-zinc-900/50 border-2 border-zinc-700">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="font-heading text-2xl font-bold uppercase mb-2">
                    Free Forever
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-5xl font-bold">$0</span>
                    <span className="text-zinc-500">/month</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2">
                    Everything you need to get started
                  </p>
                </div>
                <ul className="space-y-4 mb-8">
                  {features.free.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle
                        size={20}
                        weight="fill"
                        className="text-emerald-500 flex-shrink-0"
                      />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleGetStarted}
                  className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
                  data-testid="pricing-get-started-btn"
                >
                  Get Started Free
                </Button>
              </CardContent>
            </Card>

            {/* Add-ons */}
            <Card className="bg-zinc-900/50 border-2 border-[#FF6600]/50">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <Badge className="bg-[#FF6600] text-white mb-4">MOST FLEXIBLE</Badge>
                  <h3 className="font-heading text-2xl font-bold uppercase mb-2">
                    Pay Per Feature
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Add only what you need, cancel anytime
                  </p>
                </div>
                <div className="space-y-4 mb-8">
                  {features.addons.map((addon, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700 rounded-sm"
                    >
                      <div>
                        <p className="font-medium text-white">{addon.name}</p>
                        <p className="text-xs text-zinc-500">{addon.desc}</p>
                      </div>
                      <span className="font-heading font-bold text-[#FF6600]">
                        {addon.price}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <p className="text-sm text-zinc-500 mb-4">
                    Save 20% with annual billing
                  </p>
                  <Button
                    onClick={handleGetStarted}
                    variant="outline"
                    className="w-full h-12 border-2 border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600] hover:text-white font-heading uppercase tracking-wide"
                    data-testid="pricing-addons-btn"
                  >
                    Start Free, Add Later
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl font-bold uppercase text-center mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <Card className="bg-zinc-900/30 border border-zinc-800">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-2">Is it really free forever?</h4>
                  <p className="text-sm text-zinc-400">
                    Yes! The free plan is yours forever. You can run your business 
                    with 10 jobs/month at no cost. Only pay for add-ons if you need them.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/30 border border-zinc-800">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-2">Can I cancel add-ons anytime?</h4>
                  <p className="text-sm text-zinc-400">
                    Absolutely. All add-ons are month-to-month. Cancel anytime from 
                    your settings, no questions asked.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/30 border border-zinc-800">
                <CardContent className="p-6">
                  <h4 className="font-medium mb-2">What happens if I exceed my limits?</h4>
                  <p className="text-sm text-zinc-400">
                    You'll see a friendly prompt to upgrade. Your existing data is 
                    always accessible - we never lock you out.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t-2 border-zinc-800 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-zinc-600">
            © 2024 Free Service Pro. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
